import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { regras as api, type RegraDoCliente } from '../api/regras'
import { ErroApi } from '../api/client'
import { Aviso } from '../componentes/Aviso'
import { Botao } from '../componentes/Botao'
import { Estado } from '../componentes/Estado'
import { useSessao } from '../auth/useSessao'
import './regras.css'

/**
 * As oito decisões do cliente, editáveis.
 *
 * 🔴 **Campo tipado por regra, nunca texto livre para as oito.** O motor lê
 * booleana com `bool.TryParse`: gravar "sim" desliga o transbordo de capa dura
 * em silêncio, e a regra falha *aberta*. O servidor recusa; a tela nem oferece
 * a chance de digitar errado.
 *
 * O mesmo editor serve os dois níveis. Sem `marca`/`assistenteId` ele edita o
 * valor geral; com eles, a sobrescrita daquele assistente — e aí cada linha
 * ganha o botão de voltar a usar o geral.
 */
export function EditorDeRegras({
  marca,
  assistenteId,
  aoSalvar,
}: {
  marca?: string
  assistenteId?: number

  /** Chamado depois de gravar: é quando a pré-visualização do prompt muda. */
  aoSalvar?: () => void | Promise<void>
}) {
  const { t } = useTranslation()
  const { pode } = useSessao()

  const editavel = pode('ParametroGerenciar')
  const doAssistente = marca !== undefined && assistenteId !== undefined

  const [lista, setLista] = useState<RegraDoCliente[] | null>(null)
  const [falhou, setFalhou] = useState(false)

  const carregar = useCallback(async () => {
    setFalhou(false)
    try {
      setLista(
        doAssistente
          ? await api.doAssistente(marca!, assistenteId!)
          : await api.gerais(),
      )
    } catch {
      setFalhou(true)
    }
  }, [doAssistente, marca, assistenteId])

  useEffect(() => {
    void carregar()
  }, [carregar])

  if (falhou)
    return <Estado tipo="erro" acao={<Botao onClick={carregar}>{t('comum.tentarDeNovo')}</Botao>} />

  if (!lista) return <Estado tipo="carregando" />

  if (lista.length === 0) return <Estado tipo="vazio" descricao={t('regras.vazio')} />

  return (
    <div className="regras">
      {!editavel && <Aviso tom="info">{t('regras.somenteLeitura')}</Aviso>}

      <ul className="regras__lista">
        {lista.map((regra) => (
          <LinhaDaRegra
            key={regra.chave}
            regra={regra}
            marca={marca}
            assistenteId={assistenteId}
            editavel={editavel}
            aoMudar={async () => {
              await carregar()
              await aoSalvar?.()
            }}
          />
        ))}
      </ul>
    </div>
  )
}

function LinhaDaRegra({
  regra,
  marca,
  assistenteId,
  editavel,
  aoMudar,
}: {
  regra: RegraDoCliente
  marca?: string
  assistenteId?: number
  editavel: boolean
  aoMudar: () => Promise<void>
}) {
  const { t } = useTranslation()

  const [rascunho, setRascunho] = useState(regra.valor)
  const [erro, setErro] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)

  // O rascunho segue o servidor quando a lista é recarregada: sem isto, apagar
  // a sobrescrita deixaria o campo mostrando o valor antigo.
  useEffect(() => setRascunho(regra.valor), [regra.valor])

  const doAssistente = marca !== undefined && assistenteId !== undefined
  const sujo = rascunho !== regra.valor

  async function gravar(valor: string) {
    setErro(null)
    setEnviando(true)

    try {
      if (doAssistente) await api.gravarNoAssistente(marca!, assistenteId!, regra.chave, valor)
      else await api.gravarGeral(regra.chave, valor)

      await aoMudar()
    } catch (e) {
      // A recusa do servidor diz o que se esperava (`SEC-11`) — é ela que a
      // pessoa precisa ler, não uma mensagem genérica nossa.
      const corpo = e instanceof ErroApi ? (e.corpo as { detail?: string } | null) : null
      setErro(corpo?.detail ?? t('comum.erroDescricao'))
      setRascunho(regra.valor)
    } finally {
      setEnviando(false)
    }
  }

  /**
   * 🔴 Desligar uma booleana pede confirmação que **nomeia o efeito**.
   *
   * As três vieram de decisão do cliente em 22/06. "Deseja desativar?" não
   * informa ninguém; "livro em capa dura passa a ser respondido pelo
   * assistente" informa.
   */
  async function alternar(ligado: boolean) {
    if (!ligado && regra.efeitoAoDesligar) {
      const segue = window.confirm(
        t('regras.confirmarDesligar', { efeito: regra.efeitoAoDesligar }),
      )

      if (!segue) return
    }

    await gravar(ligado ? 'true' : 'false')
  }

  return (
    <li className="regras__item">
      <div className="regras__cabecalho">
        <div>
          <strong className="regras__nome">{t(`regras.nome.${regra.chave}`, regra.chave)}</strong>
          <p className="regras__descricao">{regra.descricao}</p>
        </div>

        <Procedencia regra={regra} />
      </div>

      {regra.tipo === 'Booleano' && (
        <label className="regras__interruptor">
          <input
            type="checkbox"
            checked={rascunho === 'true'}
            disabled={!editavel || enviando}
            onChange={(e) => void alternar(e.target.checked)}
          />
          <span>{rascunho === 'true' ? t('regras.ligada') : t('regras.desligada')}</span>
        </label>
      )}

      {regra.tipo === 'Inteiro' && (
        <div className="regras__linha-edicao">
          <label className="regras__campo">
            <span className="regras__rotulo">{t('regras.valor')}</span>
            <input
              type="number"
              inputMode="numeric"
              value={rascunho}
              min={regra.minimo ?? undefined}
              max={regra.maximo ?? undefined}
              disabled={!editavel || enviando}
              onChange={(e) => setRascunho(e.target.value)}
            />
          </label>

          {editavel && sujo && (
            <Botao variante="primario" carregando={enviando} onClick={() => void gravar(rascunho)}>
              {t('comum.salvar')}
            </Botao>
          )}
        </div>
      )}

      {regra.tipo === 'Lista' && (
        <ListaDeTermos
          regra={regra}
          rascunho={rascunho}
          editavel={editavel}
          enviando={enviando}
          aoTrocar={setRascunho}
          aoGravar={() => void gravar(rascunho)}
          sujo={sujo}
        />
      )}

      {erro && <Aviso tom="perigo">{erro}</Aviso>}

      {doAssistente && regra.procedencia === 'Assistente' && editavel && (
        <Botao
          variante="discreto"
          onClick={async () => {
            setEnviando(true)
            try {
              await api.usarOGeral(marca!, assistenteId!, regra.chave)
              await aoMudar()
            } finally {
              setEnviando(false)
            }
          }}
        >
          {t('regras.usarOGeral')}
        </Botao>
      )}
    </li>
  )
}

/**
 * De onde o valor veio.
 *
 * Sem isso, alguém edita o valor geral, nada muda porque o assistente tem
 * sobrescrita, e conclui que a tela está quebrada.
 */
function Procedencia({ regra }: { regra: RegraDoCliente }) {
  const { t } = useTranslation()

  return (
    <span className={`regras__procedencia regras__procedencia--${regra.procedencia}`}>
      {t(`regras.procedencia.${regra.procedencia}`)}

      {regra.procedencia === 'Assistente' && regra.geral !== null && (
        <small className="regras__geral">{t('regras.geralVale', { valor: regra.geral })}</small>
      )}

      {regra.atualizadoPor && (
        <small className="regras__autor">
          {t('regras.porAutor', { autor: regra.atualizadoPor })}
        </small>
      )}
    </span>
  )
}

/**
 * Lista de termos como fichas, não como texto separado por vírgula.
 *
 * O campo de texto livre devolveria os dois erros que a demanda quer impedir: a
 * vírgula esquecida que funde dois termos, e a lista apagada por inteiro — que,
 * em `familia_livros`, faz livro deixar de ser reconhecido como livro.
 */
function ListaDeTermos({
  regra,
  rascunho,
  editavel,
  enviando,
  sujo,
  aoTrocar,
  aoGravar,
}: {
  regra: RegraDoCliente
  rascunho: string
  editavel: boolean
  enviando: boolean
  sujo: boolean
  aoTrocar: (valor: string) => void
  aoGravar: () => void
}) {
  const { t } = useTranslation()
  const [novo, setNovo] = useState('')

  const termos = rascunho.split(',').map((x) => x.trim()).filter(Boolean)

  const trocar = (proximos: string[]) => aoTrocar(proximos.join(','))

  const ultimoDeLista = !regra.aceitaVazio && termos.length === 1

  return (
    <div className="regras__termos">
      <ul className="regras__fichas">
        {termos.map((termo) => (
          <li key={termo} className="regras__ficha">
            <span>{termo}</span>

            {editavel && (
              <button
                type="button"
                className="regras__remover"
                // Remover o último de uma lista de que o motor depende seria
                // colapsar a família. O servidor recusa; aqui nem se oferece.
                disabled={enviando || ultimoDeLista}
                title={ultimoDeLista ? t('regras.naoAceitaVazio') : undefined}
                aria-label={t('regras.removerTermo', { termo })}
                onClick={() => trocar(termos.filter((x) => x !== termo))}
              >
                ×
              </button>
            )}
          </li>
        ))}
      </ul>

      {editavel && (
        <div className="regras__linha-edicao">
          <label className="regras__campo">
            <span className="regras__rotulo">{t('regras.novoTermo')}</span>
            <input
              value={novo}
              disabled={enviando}
              onChange={(e) => setNovo(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return

                e.preventDefault()

                const termo = novo.trim()
                if (!termo || termos.includes(termo)) return

                trocar([...termos, termo])
                setNovo('')
              }}
            />
          </label>

          <Botao
            variante="discreto"
            onClick={() => {
              const termo = novo.trim()
              if (!termo || termos.includes(termo)) return

              trocar([...termos, termo])
              setNovo('')
            }}
          >
            {t('regras.adicionar')}
          </Botao>

          {sujo && (
            <Botao variante="primario" carregando={enviando} onClick={aoGravar}>
              {t('comum.salvar')}
            </Botao>
          )}
        </div>
      )}

      {!regra.aceitaVazio && (
        <p className="regras__nota">{t('regras.naoAceitaVazio')}</p>
      )}
    </div>
  )
}
